import React, { memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { action } from 'mobx';
import classnames from 'classnames';
import { DataSetEvents } from 'choerodon-ui/dataset/data-set/enum';
import ConfigProvider from '../config-provider';
import { getDataAttr } from './utils';
import TabsContext from './TabsContext';
const TabPane = function TabPane(props) {
    const { className, destroyInactiveTabPane, active, forceRender, rootPrefixCls, style, children, placeholder, disabled, dataSet, eventKey, ...restProps } = props;
    const { validationMap } = useContext(TabsContext);
    const dsList = dataSet ? [].concat(dataSet) : [];
    const invalidComponents = useMemo(() => new Set(), []);
    const { length } = dsList;
    const [rendered, setRendered] = useState(active);
    const prefixCls = `${rootPrefixCls}-tabpane`;
    const cls = classnames(prefixCls, active ? `${prefixCls}-active` : `${prefixCls}-inactive`, className);
    const handleValidationReport = useCallback(action((validationProps) => {
        // why validationProps is undefined?
        if (validationProps) {
            const { showInvalid, component } = validationProps;
            if (!disabled && eventKey) {
                if (showInvalid) {
                    invalidComponents.add(component);
                }
                else {
                    invalidComponents.delete(component);
                }
                validationMap.set(eventKey, invalidComponents.size === 0);
            }
        }
    }), [eventKey, disabled, invalidComponents]);
    const handleValidate = useCallback(({ valid, dataSet }) => {
        handleValidationReport({
            showInvalid: !valid,
            component: dataSet,
        });
    }, [handleValidationReport]);
    const handleReset = useCallback(({ dataSet, record }) => {
        if (record) {
            const errors = dataSet.getAllValidationErrors();
            handleValidationReport({
                showInvalid: errors.dataSet.length > 0 || errors.records.length > 0,
                component: dataSet,
            });
        }
        else {
            handleValidationReport({
                showInvalid: false,
                component: dataSet,
            });
        }
    }, []);
    const handleRemove = useCallback(({ dataSet, records }) => {
        if (records) {
            const errors = dataSet.getAllValidationErrors();
            handleValidationReport({
                showInvalid: errors.dataSet.length > 0 || errors.records.length > 0,
                component: dataSet,
            });
        }
    }, []);
    useEffect(() => () => invalidComponents.clear(), []);
    useEffect(() => {
        if (!destroyInactiveTabPane && active) {
            setRendered(true);
        }
    }, [destroyInactiveTabPane, active]);
    useEffect(() => {
        if (length && !disabled) {
            dsList.forEach(ds => ds
                .addEventListener(DataSetEvents.validate, handleValidate)
                .addEventListener(DataSetEvents.validateSelf, handleValidate)
                .addEventListener(DataSetEvents.remove, handleRemove)
                .addEventListener(DataSetEvents.reset, handleReset));
            return () => dsList.forEach(ds => ds
                .removeEventListener(DataSetEvents.validate, handleValidate)
                .removeEventListener(DataSetEvents.validateSelf, handleValidate)
                .removeEventListener(DataSetEvents.remove, handleRemove)
                .removeEventListener(DataSetEvents.reset, handleReset));
        }
    }, [active, disabled, handleValidate, handleReset, handleRemove, length, ...dsList]);
    const childrenWithProvider = length ? children : (React.createElement(ConfigProvider, { onComponentValidationReport: handleValidationReport }, children));
    return (React.createElement("div", Object.assign({ style: style, role: "tabpanel", "aria-hidden": active ? 'false' : 'true', className: cls }, getDataAttr(restProps)), forceRender || (destroyInactiveTabPane ? active : rendered) ? childrenWithProvider : placeholder));
};
TabPane.displayName = 'TabPane';
const MemoTabPane = memo(TabPane, (props, nextProps) => !nextProps.forceRender && !props.active && !nextProps.active);
MemoTabPane.defaultProps = {
    overflowCount: 99,
    showCount: true,
    hidden: false,
};
export default MemoTabPane;
